"use client";

import { usePathname } from "@/i18n/navigation";
import { useEffect, useRef } from "react";
import { GOOGLE_TAGS } from "@/config/APP/google-tags.config";

declare global {
  interface Window {
    dataLayer?: Record<string, unknown>[];
    gtag?: (...args: unknown[]) => void;
  }
}

/**
 * Sends a virtual page_view on client-side route changes so GA4/GTM track SPA navigation.
 * Also pushes to dataLayer for GTM triggers (e.g. "Page View - History Change").
 */
export function GoogleTagPageView() {
  const pathname = usePathname();
  const previousPath = useRef<string | null>(null);

  useEffect(() => {
    if (typeof window === "undefined" || pathname == null) return;

    const path = pathname || window.location.pathname;
    if (previousPath.current === path) return;
    previousPath.current = path;

    const title = document.title || "MasterUtils";
    const url = `${window.location.origin}${path}${window.location.search}`;

    // GA4 virtual page view (works when gtag is loaded by GTM or @next/third-parties)
    if (window.gtag) {
      window.gtag("event", "page_view", {
        page_path: path,
        page_title: title,
        page_location: url,
      });
      window.gtag("config", GOOGLE_TAGS.GA4_ID, {
        page_path: path,
        page_title: title,
        page_location: url,
      });
    }

    // GTM dataLayer — use this in GTM for "History Change" or custom page view tags
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      event: "virtual_page_view",
      page_path: path,
      page_title: title,
      page_location: url,
    });
  }, [pathname]);

  return null;
}
